import { AuthExpiredError } from '~/types/errors'

export interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  headers?: Record<string, string>
  body?: Record<string, unknown> | BodyInit | null
  query?: Record<string, unknown>
}

function isUnauthorized(err: unknown): boolean {
  return err instanceof Error
    && 'status' in err
    && (err as { status: number }).status === 401
}

/**
 * Detect 403 responses from `services.sensitive_ops.assert_not_acting` (#623).
 *
 * Backend returns body string `"Forbidden: Operation '<op>' is blocked while acting."`.
 * We surface a snackbar so the user sees why the action was rejected, instead of a
 * generic 403 silently bubbling up.
 */
function isSensitiveOpBlocked(err: unknown): boolean {
  if (!(err instanceof Error) || !('status' in err)) return false
  const e = err as { status: number, data?: unknown }
  if (e.status !== 403) return false
  const body = typeof e.data === 'string' ? e.data : ''
  return body.includes('is blocked while acting')
}

/**
 * Base API composable providing authenticated fetch.
 * All domain API composables should use this as their foundation.
 */
export function useApiBase() {
  const config = useRuntimeConfig()
  const tokenRefresh = useTokenRefresh()

  const apiBase = config.public.apiBase as string

  /**
   * Perform an authenticated API call.
   *
   * Behavior:
   * 1. Proactive: if accessToken の残り寿命が 5 分を切っていれば、リクエスト前に
   *    Cognito REFRESH_TOKEN_AUTH を叩いて差し替える（#427）。複数の並行リクエストが
   *    同時に来ても refresh は 1 度しか走らない（useTokenRefresh がシングルトン）。
   * 2. Reactive: それでも 401 が返った場合、refresh を 1 回試みて元のリクエストを
   *    再送する。再送でも 401 なら AuthExpiredError を throw し、#582 が持ち込んだ
   *    authStateStore でセッション切れモーダルを出す。
   */
  const authenticatedFetch = async <T>(
    url: string,
    options: ApiFetchOptions = {},
  ): Promise<T> => {
    // Proactive refresh — if this throws we'll likely hit 401 below and retry anyway.
    try {
      await tokenRefresh.refreshIfNeeded()
    }
    catch {
      // swallow; let the actual request drive the error path
    }

    const sendOnce = async (): Promise<T> => {
      const accessToken = useAuthInfo().accessToken
      const response = await $fetch<T>(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          ...options.headers,
        },
      })
      return response as T
    }

    try {
      return await sendOnce()
    }
    catch (err) {
      if (isSensitiveOpBlocked(err)) {
        // Surface the block to the user via snackbar before re-throwing (#623).
        // Non-toast handling (error overlays) still receives the original error.
        useGameStateStore().notificationQueue.push(
          'この操作は他ユーザー代行中（acting）は実行できません。',
        )
        throw err
      }
      if (!isUnauthorized(err)) throw err

      try {
        await tokenRefresh.forceRefresh()
      }
      catch {
        useAuthStateStore().markSessionExpired()
        throw new AuthExpiredError()
      }

      try {
        return await sendOnce()
      }
      catch (retryErr) {
        if (isUnauthorized(retryErr)) {
          useAuthStateStore().markSessionExpired()
          throw new AuthExpiredError()
        }
        throw retryErr
      }
    }
  }

  /**
   * Perform a public (unauthenticated) API call.
   */
  const publicFetch = async <T>(
    url: string,
    options: ApiFetchOptions = {},
  ): Promise<T> => {
    const response = await $fetch<T>(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    })
    return response as T
  }

  return { apiBase, authenticatedFetch, publicFetch }
}
